import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, ArrowRight, Verified, Crown, Building2, Globe2, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  ma: "M&A · Acquisitions", fleet: "Fleet Liquidation", aviation: "Aviation",
  maritime: "Maritime & Marine", "real-estate": "Real Estate", energy: "Energy",
  capital: "Capital · Debt · Equity", partnership: "Partnership · JV",
  talent: "Executive Talent", distressed: "Distressed Assets",
};

export default function ExchangeListingDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: "", email: "", company: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/exchange/listings/${id}`).then(r => setL(r.data)).catch(() => setL(null)).finally(() => setLoading(false));
  }, [id]);

  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post("/exchange/interest", { ...form, listing_id: id });
      setSent(true);
      toast.success("Interest registered. Counterparty will be notified.");
    } catch { toast.error("Could not send."); }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20" data-testid="listing-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!l) return (
    <div className="max-w-7xl mx-auto px-6 py-20" data-testid="listing-not-found">
      <h1 className="display-black text-3xl uppercase text-[#064E3B]">Listing not found.</h1>
      <Link to="/exchange" className="btn-ghost mt-6 inline-flex">Back to Exchange</Link>
    </div>
  );

  const p = l.posted_by_profile || {};

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="listing-detail">
      <button onClick={() => navigate(-1)} className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706] hover:underline flex items-center gap-2">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Exchange
      </button>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <article className="lg:col-span-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 bg-[#064E3B] text-white">{CATEGORY_LABELS[l.category] || l.category}</span>
            <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-[#D97706] text-[#D97706]">{l.type}</span>
            <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 bg-[#FACC15] text-[#064E3B] font-bold">{l.size}</span>
            <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-gray-300 text-gray-700">
              <Globe2 className="w-3 h-3 inline mr-1" /> {l.region}
            </span>
            {l.verified && <span className="mono text-[10px] tracking-wider uppercase px-2 py-1 bg-[#064E3B]/10 text-[#064E3B] inline-flex items-center gap-1"><Verified className="w-3 h-3" /> Verified</span>}
          </div>
          <h1 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-5 leading-[0.95] tracking-tighter">{l.title}</h1>
          <div className="mono text-[13px] text-[#D97706] mt-4 uppercase tracking-wider">{l.headline}</div>
          <div className="mt-6 prose prose-sm max-w-none text-gray-800 whitespace-pre-wrap">{l.body}</div>

          <div className="mt-10 grid grid-cols-3 gap-px bg-gray-300 border border-gray-300">
            <div className="bg-white p-5"><div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Deal Value</div><div className="display-black text-xl text-[#064E3B] mt-1">${l.deal_value_m}M</div></div>
            <div className="bg-white p-5"><div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Interest</div><div className="display-black text-xl text-[#D97706] mt-1">{l.interest_count}</div></div>
            <div className="bg-white p-5"><div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Posted</div><div className="display-black text-xl text-[#064E3B] mt-1">{l.created_at?.slice(0, 10)}</div></div>
          </div>
        </article>

        <aside className="space-y-6">
          <div className="border border-gray-300 p-6 bg-gray-50">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Posted By</div>
            <div className="mt-4 flex items-start gap-4">
              <div className="w-14 h-14 text-white flex items-center justify-center font-bold shrink-0"
                style={{ background: `hsl(${(p.avatar_seed || p.name || "x").charCodeAt(0) * 5 % 360}, 35%, 22%)` }}>
                {(p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="display-black text-lg uppercase text-[#064E3B] flex items-center gap-2">
                  {p.name}
                  {p.verified && <Verified className="w-4 h-4 text-[#064E3B]" />}
                  {p.premium && <Crown className="w-4 h-4 text-[#FACC15]" />}
                </div>
                <div className="text-sm text-gray-700">{p.title}</div>
                <div className="mono text-[11px] tracking-wider text-gray-500 uppercase mt-1 flex items-center gap-1.5">
                  <Building2 className="w-3 h-3" /> {p.company}
                </div>
                <div className="mono text-[11px] tracking-wider text-gray-500 uppercase flex items-center gap-1.5">
                  <Globe2 className="w-3 h-3" /> {p.location}
                </div>
                {p.deals_closed > 0 && (
                  <div className="mono text-[11px] tracking-wider text-[#D97706] uppercase mt-2">{p.deals_closed} closed · ${p.deal_volume_m}M</div>
                )}
              </div>
            </div>
            {p.handle && (
              <Link to={`/exchange/member/${p.handle}`} className="btn-ghost w-full mt-5 justify-center" data-testid="view-member-profile">
                View Member Profile <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          <div className="border border-[#064E3B] p-6">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Express Interest</div>
            <h3 className="display-black text-lg uppercase text-[#064E3B] mt-2">Reach the counterparty.</h3>
            {sent ? (
              <div className="mt-4 text-sm text-gray-800 bg-gray-50 p-4 border border-gray-200" data-testid="interest-success">
                <Shield className="w-4 h-4 inline mr-2 text-[#064E3B]" />
                Your message has been queued for relay. The poster typically responds within 48 hours.
              </div>
            ) : (
              <form onSubmit={send} className="mt-4 space-y-3" data-testid="interest-form">
                <input className="input-federal" required placeholder="Full name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="interest-name" />
                <input className="input-federal" required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="interest-email" />
                <input className="input-federal" placeholder="Company" value={form.company} onChange={e => setForm({...form, company: e.target.value})} data-testid="interest-company" />
                <input className="input-federal" placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="interest-phone" />
                <textarea className="input-federal" rows={4} required placeholder="Your introduction + relevant credentials" value={form.message} onChange={e => setForm({...form, message: e.target.value})} data-testid="interest-message" />
                <button type="submit" className="btn-signal w-full justify-center" data-testid="interest-submit">Send Interest <ArrowRight className="w-4 h-4" /></button>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
